/**
 * AppShell — the admin app's chrome (2026-07-09 shell wave, spec C1–C5).
 *
 * Layout tiers (spec C1/C4):
 *  - <720px:      drawer (kept mounted, transform/opacity animated) + topbar
 *                 (hamburger · page title · search icon · Quick Book icon)
 *  - 720–1079px:  64px icon rail (icons + tooltips, same role gating) + topbar
 *  - ≥1080px:     full 240px sidebar + slim 56px topbar
 *
 * The topbar is PERSISTENT at every width: current-page title (from the
 * nav metadata via navLabelKeyForPath), global customer search (C2,
 * role-gated on customers:read), Quick Book (C3 — moved here from the
 * Dashboard header so it's one tap from ANY page), today's date, and the
 * user chip (identity moved out of the sidebar footer; sign-out lives in
 * its menu).
 *
 * Counter Test: at 6:45 AM from /schedule with a wet dog at the counter,
 * search and Quick Book are one tap away without leaving the page.
 *
 * Quick Book success fires the BookingRefreshContext bump so visible
 * operational widgets (Dashboard today; Lane-D hero next) refetch —
 * Principle 1: the numbers on screen come from the source of truth.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import type { ClientConfig } from '@ferro/config';
import { useAuth } from '../contexts/AuthContext.js';
import {
  BookingRefreshProvider,
  useBookingRefresh,
} from '../contexts/BookingRefreshContext.js';
import {
  FeatureAvailabilityProvider,
  useFeatureAvailability,
} from '../contexts/FeatureAvailabilityContext.js';
import { isTextEntryTarget } from '../lib/keyboard.js';
// Sawyer 12 incident-wording-pet-notes (F3)
import { useVocabularyT } from '../lib/useTenantVocabulary.js';
import {
  NAV_SECTION_ORDER,
  navItemForPath,
  navItemIsVisible,
  navLabel,
  PLATFORM_NAV,
  rolesCanAccess,
  ROUTE_ACCESS_RULES,
  TENANT_NAV,
} from '../lib/route-permissions.js';
import { effectiveRolesOf } from '../lib/rbac-matrix.js';
import { GlobalSearch } from './GlobalSearch.js';
import './topbar-help.css'; // Sawyer 17 ai-manager-help (F5)
import { Icon } from './icons.js';
import { QuickBookModal } from './QuickBookModal.js';
// Sawyer 16 staff-messaging (E4)
import { UnreadBadge } from './team-messages/UnreadBadge.js';
// NOTE: LanguageToggle (EN/ES switcher) is intentionally NOT rendered yet.
// The bilingual admin UI is only half-translated, so the "Español" toggle
// is hidden until the ES catalog is complete. The component + i18n wiring
// stay in the tree; the user-chip menu below is its natural future home.

type AppShellProps = {
  config: ClientConfig;
};

/**
 * Human label for a staff role. The keys are the role slugs the server
 * issues; the values are i18n keys (`admin.role.<slug>`) resolved via
 * `t()`. An unknown / unmapped role falls back to the raw slug so a
 * future role still renders something legible instead of a blank chip.
 */
const ROLE_LABEL_KEYS: Record<string, string> = {
  owner: 'admin.role.owner',
  manager: 'admin.role.manager',
  receptionist: 'admin.role.receptionist',
  walker: 'admin.role.walker',
  groomer: 'admin.role.groomer',
  kennel_tech: 'admin.role.kennelTech',
  accountant: 'admin.role.accountant',
  platform_admin: 'admin.role.platformAdmin',
};

/**
 * Human-readable label for a role slug. Known slugs resolve through their
 * `admin.role.<slug>` i18n key; an unknown slug falls back to the raw
 * value so the chip never renders blank.
 */
function roleLabel(role: string, t: (key: string) => string): string {
  const key = ROLE_LABEL_KEYS[role];
  return key ? t(key) : role;
}

/**
 * Is this logo URL safe to render on the DARK navy admin sidebar?
 *
 * The sidebar background is `--brand-secondary` (#1B365D for HTHD). Most
 * tenant logos — including HTHD's `logo-full-color.png` — are full-color
 * marks built for a light/cream card and go low-contrast on dark navy.
 * Showing such a logo there is worse than the high-contrast white serif
 * wordmark, so the sidebar opts INTO an image only when the URL signals a
 * reversed / white / dark-mode variant via a documented filename token
 * (`-reversed`, `-white`, `-ondark`, `-dark`, or `-inverse`). Any other
 * logo (or no logo) → the wordmark wins on the dark sidebar. The full-color
 * logo is still used on light surfaces (customer-app, invite email, the
 * static `clients/hthd/config.ts` fallback) — this gate is sidebar-only.
 *
 * When a dark-safe HTHD logo lands (Vercel Blob upload or
 * `clients/hthd/assets/`), naming it with one of these tokens flips the
 * sidebar to the image with no further code change.
 */
const DARK_SAFE_LOGO_TOKENS = [
  '-reversed',
  '-white',
  '-ondark',
  '-on-dark',
  '-dark',
  '-inverse',
  '-inverted',
] as const;

function isDarkSafeLogo(logo: string): boolean {
  const lower = logo.toLowerCase();
  return DARK_SAFE_LOGO_TOKENS.some((token) => lower.includes(token));
}

/** Initials for the user-chip avatar ("Jane Doe" → "JD", "julian" → "J"). */
function chipInitials(
  firstName: string | null | undefined,
  lastName: string | null | undefined,
  username: string,
): string {
  const first = (firstName ?? '').trim();
  const last = (lastName ?? '').trim();
  const initials = `${first.charAt(0)}${last.charAt(0)}`.trim();
  if (initials) return initials.toUpperCase();
  return username.charAt(0).toUpperCase();
}

export function AppShell({ config }: AppShellProps): JSX.Element {
  // The refresh signal must wrap both the chrome (which bumps it) and the
  // routed pages (which subscribe), so the provider sits one level above
  // the chrome component.
  //
  // Feature availability wraps both for the same reason: the SIDEBAR reads
  // it to decide whether a provisioning-gated item exists, and the routed
  // PAGE reads it to refuse to render the surface on a direct URL. One
  // probe, one answer — a nav item and its page can never disagree about
  // whether the feature is live (#551).
  return (
    <FeatureAvailabilityProvider config={config}>
      <BookingRefreshProvider>
        <AppShellChrome config={config} />
      </BookingRefreshProvider>
    </FeatureAvailabilityProvider>
  );
}

function AppShellChrome({ config }: AppShellProps): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const { brand } = config;
  const apiUrl = config.deployment.apiUrl;
  // Sawyer 12 incident-wording-pet-notes (F3) — the Incidents nav label follows the owner's word.
  const { t: baseT, i18n } = useTranslation();
  const t = useVocabularyT(baseT, apiUrl);
  // C5 — staff comes from the reactive auth context, not a readSession()
  // snapshot: a role change or re-login re-renders the nav immediately.
  const { staff, logout } = useAuth();
  // #551 — which provisioning-gated surfaces actually exist in this
  // facility. See FeatureAvailabilityContext for why a role gate alone is
  // not enough.
  const { features } = useFeatureAvailability();
  const { bump } = useBookingRefresh();
  const [logoBroken, setLogoBroken] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [quickBookOpen, setQuickBookOpen] = useState(false);

  const hamburgerRef = useRef<HTMLButtonElement | null>(null);
  const userChipRef = useRef<HTMLDivElement | null>(null);
  const userChipButtonRef = useRef<HTMLButtonElement | null>(null);

  // The brand overlay lands asynchronously (main.tsx renders the static
  // config first, then re-renders with the DB-backed `TenantBranding`
  // overlay from `loadTenantBrand`). Without this reset, an initial 404
  // (e.g. the legacy `hthd.internationalaidesign.com/logo.png` URL that
  // is frozen/flaky) latches `logoBroken=true` forever, so a later GOOD
  // `brand.logo` never gets a second chance to load. Re-arm the <img>
  // whenever the logo URL changes so a recovered/overlaid URL can render.
  useEffect(() => {
    setLogoBroken(false);
  }, [brand.logoDark, brand.logo]);

  // CONTRAST GATE — the admin sidebar background is dark navy
  // (`--brand-secondary`, #1B365D for HTHD). Preference order:
  //   1. `brand.logoDark` — an explicitly dark-safe (reversed/white)
  //      variant; no filename gate needed, its purpose IS dark surfaces.
  //      HTHD ships one (`clients/hthd` logo-ondark.png, generated from
  //      the site's full-color mark: browns → white, brand blue kept).
  //   2. `brand.logo` — only if its FILENAME carries a dark-safe token
  //      (`-reversed`/`-white`/`-ondark`/…); most tenant logos are
  //      full-color art for light surfaces and are illegible on navy.
  //   3. The white serif wordmark — the deliberate fallback, never a
  //      silent 404.
  const sidebarLogo =
    brand.logoDark ||
    (brand.logo && isDarkSafeLogo(brand.logo) ? brand.logo : undefined);
  const showImg = !!sidebarLogo && !logoBroken;

  // C1 — current-page title from the nav metadata. Unmatched paths (the
  // 404 catch-all) fall back to the brand name. The brand WORDMARK stays
  // in the sidebar/drawer only.
  const pageNavItem = navItemForPath(location.pathname);
  const pageTitle = pageNavItem ? navLabel(t, pageNavItem) : brand.name;

  // C1 — today's date, compact, locale-aware. Derived (no i18n copy).
  //
  // #711 — the chip must track the REAL date. Pre-fix this memo was keyed
  // only on [i18n.language] and formatted `new Date()` ONCE at shell
  // mount, so a long-lived tab drifted: the 2026-07-29 owner screenshot
  // showed "Thu, Jul 23" in the header of a page whose content was
  // Wednesday, July 29. Hold `now` in state and refresh it once a minute
  // AND whenever the tab becomes visible again (background tabs get their
  // timers throttled, so the interval alone can't cover a re-focus after
  // days away).
  const [now, setNow] = useState<Date>(() => new Date());
  useEffect(() => {
    const refresh = (): void => setNow(new Date());
    const intervalId = window.setInterval(refresh, 60_000);
    const onVisibilityChange = (): void => {
      if (document.visibilityState !== 'hidden') refresh();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);
  const todayLabel = useMemo(() => {
    const locale = i18n.language === 'es' ? 'es' : 'en-US';
    return new Intl.DateTimeFormat(locale, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    }).format(now);
  }, [i18n.language, now]);

  // C2 — the topbar search is only offered to roles that can open the
  // customer surface it links into (same gate as the /customers nav item;
  // fail-closed for unknown/absent roles).
  // #428 — union over primary + granted roles (a groomer with a
  // receptionist grant gets the desk search).
  const canSearchCustomers = rolesCanAccess(
    effectiveRolesOf(staff),
    ROUTE_ACCESS_RULES['/customers'],
  );

  const handleLogout = useCallback((): void => {
    setUserMenuOpen(false);
    logout();
    navigate('/login');
  }, [logout, navigate]);

  const closeDrawer = useCallback((): void => setNavOpen(false), []);

  // C5 — Escape closes the drawer and returns focus to the hamburger.
  useEffect(() => {
    if (!navOpen) return undefined;
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return;
      setNavOpen(false);
      hamburgerRef.current?.focus();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [navOpen]);

  // C3 — `b` opens Quick Book from anywhere (guarded: never while typing).
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key !== 'b' && e.key !== 'B') return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTextEntryTarget(e.target)) return;
      e.preventDefault();
      setQuickBookOpen(true);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // User-chip menu: click-away + Escape (focus returns to the chip).
  useEffect(() => {
    if (!userMenuOpen) return undefined;
    const onPointerDown = (e: MouseEvent | TouchEvent): void => {
      if (
        userChipRef.current &&
        e.target instanceof Node &&
        userChipRef.current.contains(e.target)
      ) {
        return;
      }
      setUserMenuOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return;
      setUserMenuOpen(false);
      userChipButtonRef.current?.focus();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [userMenuOpen]);

  // C3 — a successful booking closes the modal and pings every subscribed
  // page (Dashboard widgets refetch when visible).
  const handleBooked = useCallback((): void => {
    setQuickBookOpen(false);
    bump();
  }, [bump]);

  const staffName = staff
    ? [staff.first_name, staff.last_name].filter(Boolean).join(' ') ||
      staff.username
    : '';

  return (
    <div className="ferro-admin-shell">
      {navOpen ? (
        <button
          type="button"
          className="ferro-admin-backdrop"
          aria-label={t('admin.shell.closeNav')}
          onClick={closeDrawer}
        />
      ) : null}
      <aside
        className={`ferro-admin-sidebar${navOpen ? ' ferro-admin-sidebar--open' : ''}`}
        aria-label="Primary"
      >
        <div className="ferro-admin-sidebar__brand">
          {showImg ? (
            <img
              src={sidebarLogo}
              alt={brand.name}
              className="ferro-admin-sidebar__logo"
              onError={() => setLogoBroken(true)}
            />
          ) : (
            <span className="ferro-admin-sidebar__wordmark">{brand.name}</span>
          )}
          <span className="ferro-admin-sidebar__label">Admin</span>
          {/* Rail-only compact mark (720–1079px): the full wordmark can't
              fit a 64px rail; a monogram keeps the tenant present without
              cramming. Hidden at every other width. */}
          <span className="ferro-admin-sidebar__monogram" aria-hidden="true">
            {brand.name.charAt(0)}
          </span>
        </div>
        <nav className="ferro-admin-sidebar__nav">
          {staff?.role === 'platform_admin' ? (
            <>
              <div className="ferro-admin-sidebar__section-label">Platform</div>
              {PLATFORM_NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/platform/tenants'}
                  className={({ isActive }) =>
                    `ferro-admin-sidebar__link${isActive ? ' ferro-admin-sidebar__link--active' : ''}`
                  }
                  onClick={closeDrawer}
                  title={navLabel(t, item)}
                  aria-label={navLabel(t, item)}
                >
                  <Icon name={item.icon} className="ferro-admin-sidebar__link-icon" />
                  <span className="ferro-admin-sidebar__link-label">
                    {navLabel(t, item)}
                  </span>
                </NavLink>
              ))}
              <div className="ferro-admin-sidebar__section-label">
                Tenant view (read-only)
              </div>
            </>
          ) : null}
          {/* Every tenant nav item is role-gated against the RBAC matrix
              mirror (`lib/route-permissions.ts`, scope-agnostic — see its
              header for why). A staffer only sees links to surfaces they
              can actually use; the server (#130/#156) still enforces the
              same matrix per-route, so nav-spoofing a hidden path lands on
              the RouteGuard 403, never on data they shouldn't see.

              Items are grouped into labelled sections (presentational
              only — the per-item `roleCanAccess` filter is unchanged). A
              section whose visible items are all filtered out renders
              nothing — no orphan header. On the 720–1079px rail the labels
              collapse to icons + tooltips and the section headers become
              separators; the gating is identical (spec C4). */}
          {NAV_SECTION_ORDER.map((section) => {
            const visible = TENANT_NAV.filter(
              (item) =>
                item.group === section &&
                // Two gates, both required (navItemIsVisible):
                //   · provisioning — #551: an item whose server-side
                //     feature is not configured for this facility does not
                //     exist here at all, whatever the role says. Fails
                //     closed while the probe is in flight.
                //   · role — #428: nav shows the UNION of surfaces across
                //     the primary + granted roles (the Jorge case: groomer
                //     + receptionist sees /groomer AND the desk items).
                navItemIsVisible(item, effectiveRolesOf(staff), features),
            );
            if (visible.length === 0) return null;
            return (
              <div key={section} className="ferro-admin-sidebar__section">
                <div className="ferro-admin-sidebar__section-label">
                  {t(section)}
                </div>
                {visible.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === '/'}
                    className={({ isActive }) =>
                      `ferro-admin-sidebar__link${isActive ? ' ferro-admin-sidebar__link--active' : ''}`
                    }
                    onClick={closeDrawer}
                    title={navLabel(t, item)}
                    aria-label={navLabel(t, item)}
                  >
                    <Icon
                      name={item.icon}
                      className="ferro-admin-sidebar__link-icon"
                    />
                    <span className="ferro-admin-sidebar__link-label">
                      {navLabel(t, item)}
                    </span>
                    {/* Sawyer 16 staff-messaging (E4) */}
                    {item.to === '/team-messages' ? <UnreadBadge apiUrl={apiUrl} /> : null}
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>
      </aside>

      <div className="ferro-admin-main">
        <header className="ferro-admin-topbar" data-testid="admin-topbar">
          <button
            ref={hamburgerRef}
            type="button"
            className="ferro-admin-topbar__menu"
            aria-label={navOpen ? t('admin.shell.closeNav') : t('admin.shell.openNav')}
            aria-expanded={navOpen}
            onClick={() => setNavOpen((v) => !v)}
          >
            <Icon name="menu" size={20} />
            {/* Sawyer 16 staff-messaging (E4) — the <720px unread count (review C2 fallback) */}
            <UnreadBadge apiUrl={apiUrl} placement="menu" />
          </button>
          <span className="ferro-admin-topbar__title">{pageTitle}</span>
          {canSearchCustomers ? <GlobalSearch apiUrl={apiUrl} /> : null}
          <div className="ferro-admin-topbar__actions">
            {/* Sawyer 17 ai-manager-help (F5) — one-tap Help opens the AI
                Manager ready for a how-do-I question. Same gate as the AI
                Manager nav item (desk roles); never for platform_admin, who
                renders PLATFORM_NAV and has no tenant to chat in (C9). */}
            {staff?.role !== 'platform_admin' &&
            rolesCanAccess(effectiveRolesOf(staff), ROUTE_ACCESS_RULES['/ai/manager']) ? (
              <button
                type="button"
                className="ferro-admin-topbar__help"
                aria-label={t('admin.shell.help')}
                data-testid="help-button"
                onClick={() => navigate('/ai/manager?help=1')}
              >
                <Icon name="help" />
                <span className="ferro-admin-topbar__help-label" aria-hidden="true">
                  {t('admin.shell.help')}
                </span>
              </button>
            ) : null}
            <button
              type="button"
              className="ferro-admin-topbar__quickbook"
              onClick={() => setQuickBookOpen(true)}
              data-testid="quick-book-button"
            >
              <Icon name="plus" />
              <span className="ferro-admin-topbar__quickbook-label">
                {t('admin.shell.quickBook')}
              </span>
            </button>
            <span className="ferro-admin-topbar__date">{todayLabel}</span>
            {staff ? (
              <div className="ferro-user-chip" ref={userChipRef}>
                <button
                  ref={userChipButtonRef}
                  type="button"
                  className="ferro-user-chip__button"
                  aria-label={t('admin.shell.accountMenu')}
                  aria-haspopup="menu"
                  aria-expanded={userMenuOpen}
                  onClick={() => setUserMenuOpen((v) => !v)}
                >
                  <span className="ferro-user-chip__avatar" aria-hidden="true">
                    {chipInitials(staff.first_name, staff.last_name, staff.username)}
                  </span>
                  <span className="ferro-user-chip__meta">
                    <span className="ferro-user-chip__name">{staffName}</span>
                    {staff.role ? (
                      <span className="ferro-user-chip__role">
                        {roleLabel(staff.role, t)}
                      </span>
                    ) : null}
                  </span>
                  <Icon
                    name="chevronDown"
                    size={14}
                    className="ferro-user-chip__chevron"
                  />
                </button>
                {userMenuOpen ? (
                  <div className="ferro-user-chip__menu" role="menu">
                    {/* Identity restated here so the <720px chip (avatar
                        only) still tells you who's signed in. */}
                    <div className="ferro-user-chip__menu-identity">
                      <span className="ferro-user-chip__name">{staffName}</span>
                      {staff.role ? (
                        <span className="ferro-user-chip__role">
                          {roleLabel(staff.role, t)}
                        </span>
                      ) : null}
                    </div>
                    {/* Future home of <LanguageToggle /> once the ES
                        catalog is complete (see import note). */}
                    <button
                      type="button"
                      role="menuitem"
                      className="ferro-user-chip__menu-item"
                      onClick={handleLogout}
                    >
                      <Icon name="signOut" />
                      {t('common.signOut')}
                    </button>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </header>
        <main className="ferro-admin-content">
          <Outlet />
        </main>
      </div>

      {/* C3 — Quick Book mounts at shell level so it's reachable from any
          page; success bumps the shared refresh signal. */}
      <QuickBookModal
        apiUrl={apiUrl}
        open={quickBookOpen}
        onClose={() => setQuickBookOpen(false)}
        onBooked={handleBooked}
      />
    </div>
  );
}
